import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Publish as PublishIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { useAuth } from '../../../../contexts/AuthContext';
import { 
  createPage, 
  updatePage, 
  getPage, 
  CMSPage, 
  CMSSection,
  generateSlug,
  validatePageData,
  getTemplates,
  getTemplate,
  CMSTemplate,
  applyTemplateToPage
} from '../../../../firebase/services/cmsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`page-editor-tabpanel-${index}`}
      aria-labelledby={`page-editor-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const PageEditor: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [pageData, setPageData] = useState<Partial<CMSPage>>({
    title: '',
    slug: '',
    metaDescription: '',
    metaKeywords: '',
    status: 'draft',
    template: '',
    sections: [],
    featuredImage: '',
  });
  
  const [templates, setTemplates] = useState<CMSTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CMSTemplate | null>(null);

  const [editingSection, setEditingSection] = useState<CMSSection | null>(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);

  useEffect(() => {
    loadTemplates();
    if (isEditing && id) {
      loadPage(id);
    }
  }, [id, isEditing]);

  const loadTemplates = async () => {
    try {
      const allTemplates = await getTemplates();
      setTemplates(allTemplates);
      
      // Set default template if creating new page and no template selected
      if (!isEditing && allTemplates.length > 0 && !pageData.template) {
        const defaultTemplate = allTemplates.find(t => t.name === 'Default Page') || allTemplates[0];
        if (defaultTemplate) {
          const templateData = applyTemplateToPage(defaultTemplate);
          setPageData(prev => ({
            ...prev,
            template: defaultTemplate.id,
            sections: templateData.sections || [],
          }));
          setSelectedTemplate(defaultTemplate);
        }
      }
    } catch (err: any) {
      console.error('Error loading templates:', err);
    }
  };

  const loadPage = async (pageId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const page = await getPage(pageId);
      if (page) {
        setPageData(page);
        
        // Load the selected template
        if (page.template) {
          const template = await getTemplate(page.template);
          setSelectedTemplate(template);
        }
      } else {
        setError('Page not found');
        navigate('/admin/business/online/pages');
      }
    } catch (err: any) {
      console.error('Error loading page:', err);
      setError(err.message || 'Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CMSPage, value: any) => {
    setPageData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate slug when title changes
    if (field === 'title' && !isEditing) {
      setPageData(prev => ({
        ...prev,
        slug: generateSlug(value),
      }));
    }
  };

  const handleTemplateChange = async (templateId: string) => {
    try {
      const template = await getTemplate(templateId);
      if (template) {
        const templateData = applyTemplateToPage(template);
        setPageData(prev => ({
          ...prev,
          template: templateId,
          sections: templateData.sections || [],
        }));
        setSelectedTemplate(template);
      }
    } catch (err: any) {
      console.error('Error applying template:', err);
      setError('Failed to apply template');
    }
  };

  const handleSectionEdit = (section: CMSSection) => {
    setEditingSection({ ...section });
    setSectionDialogOpen(true);
  };

  const handleSectionSave = () => {
    if (!editingSection) return;

    setPageData(prev => ({
      ...prev,
      sections: prev.sections?.map(section => 
        section.id === editingSection.id ? editingSection : section
      ) || [],
    }));

    setSectionDialogOpen(false);
    setEditingSection(null);
  };

  const handleSectionDelete = (sectionId: string) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections?.filter(section => section.id !== sectionId) || [],
    }));
  };

  const handleSectionToggleVisibility = (sectionId: string) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections?.map(section => 
        section.id === sectionId 
          ? { ...section, isVisible: !section.isVisible }
          : section
      ) || [],
    }));
  };

  const handleAddSection = () => {
    const newSection: CMSSection = {
      id: `section_${Date.now()}`,
      type: 'text',
      title: 'New Section',
      content: '<p>Enter your content here...</p>',
      settings: {},
      order: (pageData.sections?.length || 0) + 1,
      isVisible: true,
    };

    setPageData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection],
    }));
  };

  const handleSave = async (publishAfterSave: boolean = false) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate page data
      const errors = validatePageData(pageData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }

      const now = Date.now();
      const userId = userProfile?.uid || 'unknown';

      if (isEditing && id) {
        const updates: Partial<CMSPage> = {
          ...pageData,
          updatedBy: userId,
          status: publishAfterSave ? 'published' : pageData.status,
          ...(publishAfterSave && { publishedAt: now }),
        };

        await updatePage(id, updates);
        setSuccess(publishAfterSave ? 'Page published successfully!' : 'Page saved successfully!');
      } else {
        const newPageData = {
          ...pageData,
          createdBy: userId,
          updatedBy: userId,
          status: publishAfterSave ? 'published' : 'draft',
          sections: pageData.sections || [],
          ...(publishAfterSave && { publishedAt: now }),
        } as Omit<CMSPage, 'id' | 'createdAt' | 'updatedAt'>;

        const newPageId = await createPage(newPageData);
        setSuccess(publishAfterSave ? 'Page published successfully!' : 'Page created successfully!');
        
        if (!publishAfterSave) {
          navigate(`/admin/business/online/pages/${newPageId}/edit`);
        }
      }

      if (publishAfterSave) {
        setPageData(prev => ({ ...prev, status: 'published', publishedAt: now }));
      }

    } catch (err: any) {
      console.error('Error saving page:', err);
      setError(err.message || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSectionIcon = (type: string) => {
    switch (type) {
      case 'hero':
        return '🎯';
      case 'text':
        return '📝';
      case 'image_text':
        return '🖼️';
      case 'gallery':
        return '🖼️';
      case 'services':
        return '⚡';
      case 'team':
        return '👥';
      case 'timeline':
        return '📅';
      case 'contact_info':
        return '📞';
      case 'cta':
        return '🎯';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton component={Link} to="/admin/business/online/pages">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                {isEditing ? 'Edit Page' : 'Create New Page'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isEditing ? `Editing: ${pageData.title}` : 'Create a new page for your website'}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handleSave(false)}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSave(true)}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <PublishIcon />}
            >
              {pageData.status === 'published' ? 'Update & Publish' : 'Publish'}
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Main Content */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Content" />
            <Tab label="Settings" />
            <Tab label="SEO" />
          </Tabs>

          {/* Content Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Page Title"
                    value={pageData.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL Slug"
                    value={pageData.slug || ''}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    required
                    helperText="URL path for this page (e.g., 'about-us')"
                    sx={{ mb: 3 }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Page Sections */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Page Sections</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddSection}
                >
                  Add Section
                </Button>
              </Box>

              <Stack spacing={2}>
                {pageData.sections?.map((section, index) => (
                  <Card key={section.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ fontSize: '1.5rem' }}>{getSectionIcon(section.type)}</Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {section.title || `${section.type} Section`}
                            </Typography>
                            <Chip 
                              label={section.type} 
                              size="small" 
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleSectionToggleVisibility(section.id)}
                            color={section.isVisible ? 'primary' : 'default'}
                          >
                            {section.isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleSectionEdit(section)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleSectionDelete(section.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {section.content && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          dangerouslySetInnerHTML={{ 
                            __html: section.content.substring(0, 150) + (section.content.length > 150 ? '...' : '')
                          }}
                        />
                      )}
                    </CardContent>
                  </Card>
                )) || (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No sections added yet. Click "Add Section" to get started.
                  </Typography>
                )}
              </Stack>
            </Box>
          </TabPanel>

          {/* Settings Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={pageData.template || ''}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      label="Template"
                    >
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                          {template.isSystem && ' (System)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {selectedTemplate && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Template: {selectedTemplate.name}
                      </Typography>
                      {selectedTemplate.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {selectedTemplate.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={selectedTemplate.category === 'built-in' ? 'Built-in' : 'Custom'} 
                          size="small" 
                          variant="outlined"
                          color={selectedTemplate.category === 'built-in' ? 'primary' : 'secondary'}
                        />
                        <Chip 
                          label={`${selectedTemplate.sections.length} sections`} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={pageData.status || 'draft'}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Featured Image URL"
                    value={pageData.featuredImage || ''}
                    onChange={(e) => handleInputChange('featuredImage', e.target.value)}
                    helperText="URL of the main image for this page"
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* SEO Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Meta Description"
                value={pageData.metaDescription || ''}
                onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                multiline
                rows={3}
                helperText="Brief description of the page content for search engines (150-160 characters recommended)"
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Meta Keywords"
                value={pageData.metaKeywords || ''}
                onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
                helperText="Comma-separated keywords for this page"
              />
            </Box>
          </TabPanel>
        </Paper>

        {/* Section Editor Dialog */}
        <Dialog 
          open={sectionDialogOpen} 
          onClose={() => setSectionDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Edit Section: {editingSection?.title || editingSection?.type}
          </DialogTitle>
          <DialogContent>
            {editingSection && (
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Section Title"
                  value={editingSection.title || ''}
                  onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                  sx={{ mb: 3 }}
                />

                <Typography variant="subtitle2" gutterBottom>
                  Content
                </Typography>
                <ReactQuill
                  value={editingSection.content || ''}
                  onChange={(content) => setEditingSection({ ...editingSection, content })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link', 'image'],
                      ['clean']
                    ],
                  }}
                  style={{ height: '200px', marginBottom: '50px' }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSectionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSectionSave} variant="contained">Save Section</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default PageEditor; 